import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setBranchHeader } from "../lib/api";
import { useAuth } from "./AuthContext";

const BranchCtx = createContext(null);
const STORAGE_KEY = "kheffah_branch_id";

export function BranchProvider({ children }) {
  const { user, loading } = useAuth();
  const [state, setState] = useState({ branches: [], entitlement: null, currentId: null, loaded: false });

  const reload = useCallback(async () => {
    if (loading) return;
    if (!user || user.role === "SUPER_ADMIN") {
      setBranchHeader(null);
      setState({ branches: [], entitlement: null, currentId: null, loaded: true });
      return;
    }
    try {
      const { data } = await api.get("/branches");
      const stored = localStorage.getItem(STORAGE_KEY);
      const valid = data.branches.some((b) => b.id === stored);
      if (stored && !valid) {
        // Stale selection (branch removed / access revoked): drop it and re-scope the page.
        localStorage.removeItem(STORAGE_KEY);
        setBranchHeader(null);
        window.location.reload();
        return;
      }
      const currentId = valid ? stored : data.current_branch_id;
      setBranchHeader(currentId);
      setState({ branches: data.branches, entitlement: data.entitlement || null, currentId, loaded: true });
    } catch {
      if (localStorage.getItem(STORAGE_KEY)) {
        localStorage.removeItem(STORAGE_KEY);
        setBranchHeader(null);
        window.location.reload();
        return;
      }
      setBranchHeader(null);
      setState({ branches: [], entitlement: null, currentId: null, loaded: true });
    }
  }, [user, loading]);

  useEffect(() => { reload(); }, [reload]);

  const selectBranch = useCallback((id) => {
    localStorage.setItem(STORAGE_KEY, id);
    setBranchHeader(id);
    // Pages fetch their data on mount; a reload is the simplest way to re-scope every page.
    window.location.reload();
  }, []);

  const value = useMemo(() => {
    const current = state.branches.find((b) => b.id === state.currentId) || null;
    const active = state.branches.filter((b) => b.active !== false);
    return { ...state, current, activeBranches: active, showSelector: active.length > 1, selectBranch, reload };
  }, [state, selectBranch, reload]);

  return <BranchCtx.Provider value={value}>{children}</BranchCtx.Provider>;
}

export const useBranch = () => useContext(BranchCtx);
